import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import axios from "./AxiosInstance.jsx";
import Boutton from "./Button.jsx";

const COUNTRY_COLORS = {
    "Zhongo": "#ff6b6b",
    "Eurocorp": "#3b82f6",
    "Petrolistan": "#f59e0b",
    "Volgovie": "#6b7280",
    "Autoristan": "#8b5cf6",
    "Sudica": "#f97316",
    "Neo Atlas": "#9ca3af",
    "Bricologia": "#10b981"
};

const EcoEnergyStats = ({ gameState, mobile, setActiveStatView }) => {
    const [ecoStats, setEcoStats] = useState({});
    const [energyStats, setEnergyStats] = useState({});
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        setIsLoading(true);
        const fetchStats = async () => {
            try {
                const ecoRes = await axios.get("/stats/eco");
                setEcoStats(ecoRes.data);
                const energyRes = await axios.get("/stats/energy");
                setEnergyStats(energyRes.data);
            } catch (err) {
                console.error("Erreur chargement stats eco et energie", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
    }, []);

    useEffect(() => {
        if (gameState?.joueur?.short_name) {
            setSelectedCountries([gameState.joueur.short_name]);
        }
    }, [gameState]);

    const sectors = ["Industry", "Agriculture", "Services", "technology"];

    const radarData = sectors.map(subject => {
        const entry = { subject };
        selectedCountries.forEach(country => {
            const data = ecoStats[country];
            if (data) {
                const key = subject.toLowerCase() + "_share";
                entry[country] = parseFloat(data[key]) || 0;
            }
        });
        return entry;
    });

    const radarSustainability = sectors.map(subject => {
        const entry = { subject };
        selectedCountries.forEach(country => {
            const data = ecoStats[country];
            if (data) {
                const key = subject.toLowerCase() + "_sustainability";
                entry[country] = parseFloat(data[key]) || 0;
            }
        });
        return entry;
    });

    const energySources = ["coal", "oil", "gas", "nuclear", "hydro", "solar", "wind", "geothermal"];
    const barEnergyData = energySources.map(source => {
        const entry = { source };
        selectedCountries.forEach(country => {
            const data = energyStats[country];
            if (data) {
                entry[country] = data[source];
            }
        });
        return entry;
    });

    const getMaxRadarValue = (data, selectedCountries) => {
        let max = 0;
        for (const entry of data) {
            for (const country of selectedCountries) {
                if (entry[country] && entry[country] > max) {
                    max = entry[country];
                }
            }
        }
        return Math.ceil(max);
    };

    const maxRadarValue = getMaxRadarValue(radarData, selectedCountries);
    const maxSustainabilityValue = getMaxRadarValue(radarSustainability, selectedCountries);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
                    <p className="text-slate-300 text-lg">Chargement des statistiques...</p>
                </div>
            </div>
        );
    }

    const allCountries = Object.keys(ecoStats);

    return (
        <div className="min-h-screen bg-slate-900">
            <div className="flex justify-center space-x-4 p-4">
                <button
                    onClick={() => { mobile === 'PC' ? navigate("/stats/countries") : setActiveStatView('general') }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium bg-slate-700 text-slate-300 hover:bg-slate-600`}
                >
                    Impacts par année
                </button>
                <button
                    onClick={() => { mobile === 'PC' ? navigate("/stats/ecoEnergy") : setActiveStatView('eco_energy') }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white `}
                >
                    Économie & Énergie
                </button>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                {/* Sélection des pays */}
                <div className="mb-8">
                    <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-700/50 shadow-lg">
                        <h2 className="text-lg sm:text-xl font-semibold text-white mb-4 flex items-center">
                            <span className="text-2xl mr-2">🌍</span>
                            Sélectionner les pays
                        </h2>

                        {/* Boutons de sélection rapide */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            <button
                                onClick={() => setSelectedCountries(allCountries)}
                                className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium"
                            >
                                Tout sélectionner
                            </button>
                            <button
                                onClick={() => setSelectedCountries([])}
                                className="px-3 py-1.5 text-sm bg-slate-600 hover:bg-slate-700 text-white rounded-lg transition-colors duration-200 font-medium"
                            >
                                Tout désélectionner
                            </button>
                        </div>

                        {/* Grille des pays */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {allCountries.map(country => (
                                <label
                                    key={country}
                                    className={`
                                        flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 group
                                        ${selectedCountries.includes(country)
                                            ? 'border-blue-500 bg-blue-500/10'
                                            : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                                        }
                                    `}
                                >
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            value={country}
                                            checked={selectedCountries.includes(country)}
                                            onChange={() => {
                                                if (selectedCountries.includes(country)) {
                                                    setSelectedCountries(selectedCountries.filter(c => c !== country));
                                                } else {
                                                    setSelectedCountries([...selectedCountries, country]);
                                                }
                                            }}
                                            className="sr-only"
                                        />
                                        <div className={`
                                            w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200
                                            ${selectedCountries.includes(country)
                                                ? 'border-blue-500 bg-blue-500'
                                                : 'border-slate-400 group-hover:border-slate-300'
                                            }
                                        `}>
                                            {selectedCountries.includes(country) && (
                                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                    <div className="ml-3 flex items-center">
                                        <div
                                            className="w-3 h-3 rounded-full mr-2"
                                            style={{ backgroundColor: COUNTRY_COLORS[country] }}
                                        ></div>
                                        <span className="text-white font-medium text-sm">{country}</span>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Graphiques */}
                {selectedCountries.length === 0 ? (
                    <div className="text-center py-12">
                        <div className="text-6xl mb-4">📈</div>
                        <h3 className="text-xl font-semibold text-slate-300 mb-2">
                            Aucun pays sélectionné
                        </h3>
                        <p className="text-slate-400">
                            Sélectionnez au moins un pays pour voir les statistiques
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-700/50 shadow-lg">
                            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center">
                                <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mr-3"></div>
                                Part des secteurs
                            </h3>
                            <div className="h-64 sm:h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart data={radarData}>
                                        <PolarGrid stroke="#475569" opacity={0.3} />
                                        <PolarAngleAxis dataKey="subject" stroke="#94a3b8" fontSize={12} />
                                        <PolarRadiusAxis domain={[0, maxRadarValue]} stroke="#94a3b8" fontSize={12} />
                                        {selectedCountries.map((country, i) => (
                                            <Radar
                                                key={country}
                                                name={country}
                                                dataKey={country}
                                                stroke={COUNTRY_COLORS[country]}
                                                fill={COUNTRY_COLORS[country]}
                                                fillOpacity={0.3}
                                            />
                                        ))}
                                        <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1e293b',
                                                border: '1px solid #475569',
                                                borderRadius: '8px',
                                                color: '#f1f5f9'
                                            }}
                                        />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-700/50 shadow-lg">
                            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center">
                                <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mr-3"></div>
                                Durabilité des secteurs
                            </h3>
                            <div className="h-64 sm:h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadarChart data={radarSustainability}>
                                        <PolarGrid stroke="#475569" opacity={0.3} />
                                        <PolarAngleAxis dataKey="subject" stroke="#94a3b8" fontSize={12} />
                                        <PolarRadiusAxis domain={[0, maxSustainabilityValue]} stroke="#94a3b8" fontSize={12} />
                                        {selectedCountries.map((country) => (
                                            <Radar
                                                key={country}
                                                name={country}
                                                dataKey={country}
                                                stroke={COUNTRY_COLORS[country]}
                                                fill={COUNTRY_COLORS[country]}
                                                fillOpacity={0.3}
                                            />
                                        ))}
                                        <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1e293b',
                                                border: '1px solid #475569',
                                                borderRadius: '8px',
                                                color: '#f1f5f9'
                                            }}
                                        />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="md:col-span-2 bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-700/50 shadow-lg">
                            <h3 className="text-lg sm:text-xl font-bold text-white mb-4 flex items-center">
                                <div className="w-1 h-6 bg-gradient-to-b from-blue-400 to-purple-500 rounded-full mr-3"></div>
                                Mix énergétique
                            </h3>
                            <div className="h-64 sm:h-80">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={barEnergyData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#475569" opacity={0.3} />
                                        <XAxis
                                            dataKey="source"
                                            stroke="#94a3b8"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <YAxis
                                            stroke="#94a3b8"
                                            fontSize={12}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#1e293b',
                                                border: '1px solid #475569',
                                                borderRadius: '8px',
                                                color: '#f1f5f9'
                                            }}
                                        />
                                        <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                                        {selectedCountries.map((country, i) => (
                                            <Bar
                                                key={country}
                                                dataKey={country}
                                                fill={COUNTRY_COLORS[country]}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EcoEnergyStats;